import React, { useEffect, useRef, useState } from 'react';

import {
  Dialog,
  DialogContent,
  DialogFooter,
  H2,
  H5,
  Input,
  NewButton,
  VStack,
  useToast,
} from '@eluve/components';
import { getTiptapMarkdownContentFromEditor } from '@eluve/markdown-utils';

import { EluveEditor, EluveEditorHandle } from '../../../EluveEditor';

export interface Snippet {
  id?: string | unknown;
  title: string;
  content: string;
}

export interface SnippetModalProps {
  snippet?: Snippet;
  isOpen: boolean;
  onOpenChange?(open: boolean): void;
  onClose?: () => void;
  onSave?: (title: string, content: string) => void;
}

export const SnippetModal: React.FC<SnippetModalProps> = ({
  snippet,
  isOpen,
  onOpenChange,
  onClose,
  onSave,
}) => {
  const toast = useToast();
  const [title, setTitle] = useState(snippet?.title ?? '');
  const [content, setContent] = useState(snippet?.content ?? '');
  const [isSaving, setIsSaving] = useState(false);
  const editorRef = useRef<EluveEditorHandle | null>(null);

  useEffect(() => {
    if (isOpen) {
      setTitle(snippet?.title ?? '');
      setContent(snippet?.content ?? '');
    }
  }, [isOpen]);

  const handleSave = () => {
    try {
      setIsSaving(true);
      let savedContent = content;
      if (editorRef.current) {
        // Save the snippet content in markdown
        savedContent = editorRef.current.getMarkdown();
      }
      onSave?.(title, savedContent);
    } catch (e) {
      toast.error('Failed to save snippet');
    } finally {
      setIsSaving(false);
    }
  };

  const handleClose = () => {
    onOpenChange?.(false);
    onClose?.();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="flex flex-col gap-4 sm:max-w-[700px]">
        <H2>{snippet?.title ? 'Edit Snippet' : 'Create Snippet'}</H2>
        <VStack gap={3}>
          <H5>Title</H5>
          <Input
            className="rounded-lg border border-border-primary"
            placeholder="Snippet title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
        </VStack>
        <VStack gap={3}>
          <H5>Content</H5>
          <EluveEditor
            className="h-[200px] w-full overflow-y-auto rounded-lg border border-border-primary"
            ref={editorRef}
            content={snippet?.content}
            onUpdate={(editor) => {
              setContent(getTiptapMarkdownContentFromEditor(editor));
            }}
          />
        </VStack>
        <DialogFooter className="flex w-full justify-end">
          <NewButton type="outline" onClick={handleClose} text="Cancel" />
          <NewButton
            disabled={!title || !content || isSaving}
            onClick={handleSave}
            text="Save"
          />
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
